import { action } from 'mobx';
import { SessionStore } from './session.store';
import { AggregateFunction, AgtAnalyticsStore, SelectColumn } from './agt.analytics.store';

export interface IAgtFacebookModel {
	vtnrId: number;
	bm: number;
	facebookUrl: string;
	seitenStatus: number;
	fananzahl: number;
	abonnenten: number;
	postinganzahl: number;
	erreichtePersonen: number;
	sichtkontakte: number;
	aboPr: number;
	abo1890: number;
	vtEigenePosts: number;
}

export class AgtFacebookModel implements IAgtFacebookModel {
	vtnrId: number;
	bm: number;
	facebookUrl: string;
	seitenStatus: number;
	fananzahl: number;
	abonnenten: number;
	postinganzahl: number;
	erreichtePersonen: number;
	sichtkontakte: number;
	aboPr: number;
	abo1890: number;
	vtEigenePosts: number;

	// Legacy compatibility properties
	agtId: number;
	j: any;

	constructor(data: any) {
		this.vtnrId = data.aid || 0;
		this.bm = data.bm ? parseInt(data.bm) : 0;
		this.facebookUrl = data.u || '';
		this.seitenStatus = data.s || 0;
		this.fananzahl = data.f || 0;
		this.abonnenten = data.b || 0;
		this.postinganzahl = data.p || 0;
		this.erreichtePersonen = data.r || 0;
		this.sichtkontakte = data.v || 0;
		this.aboPr = data.a || 0;
		this.abo1890 = data.o || 0;
		this.vtEigenePosts = data.w || 0;

		// Legacy compatibility - alias properties
		this.agtId = this.vtnrId;

		// Legacy j property for backwards compatibility
		this.j = {
			u: this.facebookUrl,
			s: this.seitenStatus,
			f: this.fananzahl,
			b: this.abonnenten,
			p: this.postinganzahl,
			r: this.erreichtePersonen,
			v: this.sichtkontakte,
			a: this.aboPr,
			o: this.abo1890,
			w: this.vtEigenePosts
		};
	}
}

export class AgtFacebookStore extends AgtAnalyticsStore {
	constructor(session: SessionStore) {
		super(session, {
			tableName: 'agentur_facebook',
			defaultColumns: [
				{ column: 'vtnr_id', aggregateFunction: AggregateFunction.NONE, alias: 'aid' },
				{ column: 'bm', aggregateFunction: AggregateFunction.NONE },
				{ column: 'facebook_url', aggregateFunction: AggregateFunction.NONE, alias: 'u' },
				{ column: 'seiten_status', aggregateFunction: AggregateFunction.NONE, alias: 's' },
				{ column: 'fananzahl', aggregateFunction: AggregateFunction.NONE, alias: 'f' },
				{ column: 'abonnenten', aggregateFunction: AggregateFunction.NONE, alias: 'b' },
				{ column: 'postinganzahl', aggregateFunction: AggregateFunction.NONE, alias: 'p' },
				{ column: 'erreichte_personen', aggregateFunction: AggregateFunction.NONE, alias: 'r' },
				{ column: 'sichtkontakte', aggregateFunction: AggregateFunction.NONE, alias: 'v' },
				{ column: 'abo_pr', aggregateFunction: AggregateFunction.NONE, alias: 'a' },
				{ column: 'abo_1890', aggregateFunction: AggregateFunction.NONE, alias: 'o' },
				{ column: 'vt_eigene_posts', aggregateFunction: AggregateFunction.NONE, alias: 'w' },
			],
		});
		this.queryName = '?facebook';
	}

	async getDistinctBMs(): Promise<number[]> {
		const selectColumns: SelectColumn[] = [
			{ column: 'bm', aggregateFunction: AggregateFunction.NONE, distinct: true },
		];
		const results = await this.query(selectColumns);
		return results.map(data => parseInt(data.bm)).sort((a, b) => a - b);
	}

	@action
	async loadAgtByBm(agtId: number, bm: number): Promise<AgtFacebookModel[]> {
		if (!bm || bm <= 0) {
			return [];
		}
		let whereClause = 'a.vtnr_id = ' + agtId + ' AND bm = ' + bm;
		const results = await this.query(this.config.defaultColumns, whereClause);
		return results.map(data => new AgtFacebookModel(data));
	}

	@action
	async aggreagteYtd(bm: number): Promise<any[]> {
		let firstBmOfYear = Math.floor(bm / 100) * 100 + 1;
		let whereClause = `bm >= ${firstBmOfYear} AND bm <= ${bm}`;
		if (!bm || bm <= 0) {
			return [];
		}
		const selectColumns: SelectColumn[] = [
			{ column: 'vtnr_id', aggregateFunction: AggregateFunction.NONE, alias: 'aid', groupBy: true },
			{ column: 'postinganzahl', aggregateFunction: AggregateFunction.SUM, alias: 'p', },
			{ column: 'vt_eigene_posts', aggregateFunction: AggregateFunction.SUM, alias: 'w', },
		];

		return await this.query(selectColumns, whereClause);

	}

	@action
	async loadByBmRange(minBm: number, maxBm: number): Promise<AgtFacebookModel[]> {
		if (!minBm || !maxBm || minBm <= 0 || maxBm <= 0) {
			return [];
		}
		// raw monthly rows, no ytd merge -> sums are computed client-side over the range
		const whereClause = ` bm >= ${minBm} AND bm <= ${maxBm} `;
		const results = await this.query(this.config.defaultColumns, whereClause);
		return results.map((data) => new AgtFacebookModel(data));
	}

	@action
	async loadByBm(bm: number): Promise<AgtFacebookModel[]> {
		if (!bm || bm <= 0) {
			return [];
		}
		let whereClause = ` bm = ${bm} `;


		const [results, aggForBM] = await Promise.all([
			this.query(this.config.defaultColumns, whereClause),
			this.aggreagteYtd(bm)
		]);

		// Merge aggregated YTD data into results where aid matches and bm equals bm
		return results.map(data => {
			if (data.bm === bm) {
				const agg = aggForBM.find(a => a.aid === data.aid);
				if (agg) {
					// Merge aggregated values into the result
					return new AgtFacebookModel({
						...data,
						p: agg.p, // postinganzahl ytd
						w: agg.w // vt_eigene_posts ytd
					});
				}
			}
			return new AgtFacebookModel(data);
		});
	}
}
